import { useFrame, useThree } from '@react-three/fiber'
import { useEffect, useRef } from 'react'

const ACCUMULATIVE_SHADOW_SETTLE_FRAMES = 100

interface Props {
    geometryKey: string
}

/**
 * Stage accumulates its contact shadow over 100 frames. After that, embedded
 * viewers can keep the real-time shadow map frozen while only design pixels
 * change. Geometry-key changes restart the bake; camera movement requests a
 * one-frame shadow refresh from CameraRig.
 */
export default function EmbeddedShadowMap(props:Props) {
    const gl = useThree(state => state.gl)
    const settledFrames = useRef(0)
    const freezeFrame = useRef<number>()

    useEffect(() => {
        settledFrames.current = 0
        gl.shadowMap.autoUpdate = true
        gl.shadowMap.needsUpdate = true

        return () => {
            if(freezeFrame.current !== undefined) {
                cancelAnimationFrame(freezeFrame.current)
                freezeFrame.current = undefined
            }
            gl.shadowMap.autoUpdate = true
            gl.shadowMap.needsUpdate = true
        }
    }, [gl.shadowMap, props.geometryKey])

    useFrame(() => {
        if(!gl.shadowMap.autoUpdate) return

        settledFrames.current += 1
        if(
            settledFrames.current < ACCUMULATIVE_SHADOW_SETTLE_FRAMES
            || freezeFrame.current !== undefined
        ) {
            return
        }

        // Freeze after the current R3F frame finishes, so the final temporal
        // accumulation sample and its normal scene render both complete.
        freezeFrame.current = requestAnimationFrame(() => {
            freezeFrame.current = undefined
            gl.shadowMap.autoUpdate = false
        })
    })

    return null
}
